import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-white mt-8 mb-3">{children}</h2>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm sm:text-base leading-relaxed text-gray-200/90">{children}</p>
);

const List = ({ children }: { children: React.ReactNode }) => (
  <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-gray-200/90">{children}</ul>
);

const TermsOfService: React.FC = () => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10"
    >
      <Helmet>
        <title>Terms of Service | Fantasy Fiesta</title>
        <meta name="description" content="Read the terms for using Fantasy Fiesta, including acceptable use, integrations, and disclaimers." />
        <link rel="canonical" href="https://fantasy-fiesta.com/terms-of-service" />
      </Helmet>
      <div className="rounded-3xl border border-white/10 bg-black/70 backdrop-blur p-6 sm:p-8 shadow-xl">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Terms of Service</h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-2">Last updated: {new Date().toLocaleDateString()}</p>
        </header>

        <Paragraph>
          These Terms of Service ("Terms") govern your access to and use of Fantasy Fiesta, including the
          draft assistant, player rankings, trade calculator, and league integrations. By using the service,
          you agree to these Terms.
        </Paragraph>

        <SectionHeading>1. Eligibility</SectionHeading>
        <Paragraph>You must be at least 13 years old to use the service.</Paragraph>

        <SectionHeading>2. Your Account</SectionHeading>
        <List>
          <li>You are responsible for the activity that occurs under your account.</li>
          <li>Keep your credentials secure and notify us of any unauthorized use.</li>
          <li>Provide accurate information and keep it up to date.</li>
        </List>

        <SectionHeading>3. Integrations and League Data</SectionHeading>
        <Paragraph>
          If you connect third-party services (e.g., Sleeper), you authorize us to access and process your
          league data solely to provide features like draft recommendations, roster insights, and trade
          analysis. You may disconnect an integration at any time.
        </Paragraph>

        <SectionHeading>4. Acceptable Use</SectionHeading>
        <List>
          <li>Do not abuse, interfere with, or disrupt the service or its security.</li>
          <li>Do not scrape, reverse engineer, or use automated means outside documented APIs.</li>
          <li>Do not infringe intellectual property or violate applicable laws.</li>
        </List>

        <SectionHeading>5. Content and IP</SectionHeading>
        <Paragraph>
          The service, including text, graphics, logos, and software, is owned by Fantasy Fiesta or our
          licensors and is protected by intellectual property laws. You are granted a limited, non-exclusive,
          non-transferable license to use the service for personal, non-commercial purposes.
        </Paragraph>

        <SectionHeading>6. Beta Features and Updates</SectionHeading>
        <Paragraph>
          We may offer experimental or beta features that may change or be discontinued. We may update the
          service automatically to improve performance, security, or functionality.
        </Paragraph>

        <SectionHeading>7. Disclaimers</SectionHeading>
        <Paragraph>
          The service is provided "as is" and "as available" without warranties of any kind. Fantasy data
          and projections are inherently uncertain; decisions you make using the service are your
          responsibility.
        </Paragraph>

        <SectionHeading>8. Limitation of Liability</SectionHeading>
        <Paragraph>
          To the fullest extent permitted by law, Fantasy Fiesta and its affiliates will not be liable for
          indirect, incidental, special, consequential, exemplary, or punitive damages, or for loss of
          profits, data, or goodwill.
        </Paragraph>

        <SectionHeading>9. Termination</SectionHeading>
        <Paragraph>
          We may suspend or terminate your access if you violate these Terms or for conduct that risks harm
          to other users or the service. You may stop using the service at any time.
        </Paragraph>

        <SectionHeading>10. Governing Law</SectionHeading>
        <Paragraph>
          These Terms are governed by the laws of the United States and, where applicable, the state where
          the company is organized, without regard to conflict of laws principles.
        </Paragraph>

        <SectionHeading>11. Changes to These Terms</SectionHeading>
        <Paragraph>
          We may modify these Terms from time to time. We will post the updated Terms and update the “Last
          updated” date above. Continued use after changes means you accept the new Terms.
        </Paragraph>

        {/* Contact section intentionally omitted until a public contact channel is available. */}
      </div>
    </motion.section>
  );
};

export default TermsOfService;
